import { useEffect, useState } from "react";
import { axiosInstance } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ShoppingCart, RotateCcw, DollarSign, FileText, Wallet, Clock } from "lucide-react";

const fmt = (n) => `₡${Number(n || 0).toLocaleString("es-CR", { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-CR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const KindBadge = ({ kind, sale }) => {
  const config = {
    sale: { icon: ShoppingCart, label: "Venta", color: "bg-blue-100 text-blue-700" },
    credit_note: { icon: RotateCcw, label: "Nota de Crédito", color: "bg-amber-100 text-amber-700" },
    payment: { icon: DollarSign, label: "Pago", color: "bg-emerald-100 text-emerald-700" },
    debt_created: { icon: Wallet, label: "Cuenta por cobrar", color: "bg-rose-100 text-rose-700" },
    debt_closed: { icon: Wallet, label: "Cuenta cancelada", color: "bg-emerald-100 text-emerald-700" },
  };
  const c = config[kind] || { icon: Clock, label: kind, color: "bg-slate-100 text-slate-600" };
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium ${c.color}`}>
      <Icon className="w-3 h-3" />
      {c.label}
      {sale?.status === "voided" && <span className="ml-1 text-red-600">(ANULADA)</span>}
    </span>
  );
};

export default function CustomerHistoryDialog({ open, onOpenChange, customerId, customerName, boxId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchHistory = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (boxId) params.set("box_id", boxId);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      const r = await axiosInstance.get(`/customers/${customerId}/history?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      setData({ error: err?.response?.data?.detail || "Error al cargar historial" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && customerId) fetchHistory();
  }, [open, customerId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="customer-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-600" />
            Historial de {customerName || data?.customer?.name || "cliente"}
          </DialogTitle>
        </DialogHeader>

        {/* Filtros por fecha */}
        <div className="grid grid-cols-3 gap-2 items-end">
          <div>
            <Label className="text-xs">Desde</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-8" data-testid="hist-start-date" />
          </div>
          <div>
            <Label className="text-xs">Hasta</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-8" data-testid="hist-end-date" />
          </div>
          <Button size="sm" variant="outline" onClick={fetchHistory} disabled={loading} data-testid="hist-apply-btn">
            {loading ? "Cargando..." : "Aplicar"}
          </Button>
        </div>

        {/* Totales */}
        {data?.totals && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="rounded border bg-blue-50 px-2 py-1.5 text-center">
              <div className="text-[10px] uppercase text-slate-500">Comprado</div>
              <div className="font-bold text-blue-700 text-sm" data-testid="hist-total-purchased">{fmt(data.totals.total_purchased)}</div>
            </div>
            <div className="rounded border bg-amber-50 px-2 py-1.5 text-center">
              <div className="text-[10px] uppercase text-slate-500">Devuelto</div>
              <div className="font-bold text-amber-700 text-sm" data-testid="hist-total-returned">{fmt(data.totals.total_returned)}</div>
            </div>
            <div className="rounded border bg-emerald-50 px-2 py-1.5 text-center">
              <div className="text-[10px] uppercase text-slate-500">Pagado</div>
              <div className="font-bold text-emerald-700 text-sm" data-testid="hist-total-paid">{fmt(data.totals.total_paid)}</div>
            </div>
            <div className={`rounded border px-2 py-1.5 text-center ${data.totals.current_balance > 0 ? "bg-rose-50" : "bg-slate-50"}`}>
              <div className="text-[10px] uppercase text-slate-500">Saldo actual</div>
              <div className={`font-bold text-sm ${data.totals.current_balance > 0 ? "text-rose-700" : "text-slate-700"}`} data-testid="hist-current-balance">{fmt(data.totals.current_balance)}</div>
            </div>
          </div>
        )}

        {data?.error && <p className="text-red-600 text-sm">{data.error}</p>}

        {/* Timeline */}
        <div className="max-h-96 overflow-y-auto space-y-2 border-t pt-2" data-testid="hist-timeline">
          {loading && <p className="text-center text-sm text-slate-500 py-4">Cargando...</p>}
          {!loading && data?.events?.length === 0 && (
            <p className="text-center text-sm text-slate-500 py-4">Sin movimientos en este período</p>
          )}
          {!loading && (data?.events || []).map((e, idx) => (
            <div key={`${e.kind}-${idx}`} className="border-l-2 pl-3 pb-2" style={{ borderColor: e.kind === "sale" ? "#3b82f6" : e.kind === "credit_note" ? "#f59e0b" : e.kind === "payment" ? "#10b981" : "#e11d48" }}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <KindBadge kind={e.kind} sale={e} />
                    <span className="text-xs text-slate-500">{fmtDate(e.date)}</span>
                    {e.document_number && <span className="text-xs font-mono text-slate-600">{e.document_number}</span>}
                  </div>
                  {e.kind === "sale" && (
                    <div className="mt-1 text-xs text-slate-600">
                      {(e.payment_method || "").toUpperCase()} · {(e.products || []).length} producto(s)
                      {e.products?.length > 0 && (
                        <span className="ml-1 text-slate-400">
                          ({(e.products || []).slice(0, 3).map((p) => `${p.name}×${p.quantity}`).join(", ")}{e.products.length > 3 ? "..." : ""})
                        </span>
                      )}
                    </div>
                  )}
                  {e.kind === "credit_note" && (
                    <div className="mt-1 text-xs text-slate-600">
                      {e.type === "partial" ? "Devolución parcial" : "Anulación completa"}
                      {e.reason && <span className="ml-1 italic">— {e.reason}</span>}
                    </div>
                  )}
                  {e.kind === "payment" && (
                    <div className="mt-1 text-xs text-slate-600">
                      {(e.transaction_type || "").toUpperCase()} · Saldo restante: {fmt(e.remaining)}
                    </div>
                  )}
                  {e.kind === "debt_created" && e.description && (
                    <div className="mt-1 text-xs text-slate-600 truncate">{e.description}</div>
                  )}
                  {e.kind === "debt_closed" && (
                    <div className="mt-1 text-xs text-emerald-700 italic">Cuenta cancelada — saldo en 0</div>
                  )}
                </div>
                {e.amount != null && (
                  <div className="text-right shrink-0">
                    <div className={`text-sm font-bold ${
                      e.kind === "sale" ? (e.status === "voided" ? "text-slate-400 line-through" : "text-blue-700") :
                      e.kind === "credit_note" ? "text-amber-700" :
                      e.kind === "payment" ? "text-emerald-700" :
                      "text-rose-700"
                    }`}>
                      {e.kind === "credit_note" || e.kind === "payment" ? "-" : "+"}{fmt(e.amount)}
                    </div>
                    {e.created_by && <div className="text-[10px] text-slate-400">por {e.created_by}</div>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {data?.counts && (
          <div className="text-xs text-slate-500 border-t pt-2 flex gap-3 flex-wrap">
            <span>Ventas: <b>{data.counts.sales}</b></span>
            <span>Notas de crédito: <b>{data.counts.credit_notes}</b></span>
            <span>Deudas: <b>{data.counts.debts}</b></span>
            <span>Pagos: <b>{data.counts.payments}</b></span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
